import { c as _c } from "react/compiler-runtime";
import { differenceInDays, startOfDay } from 'date-fns';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { type GraphQLLetter, GraphQLLetterDirection, GraphQLLetterFriendlyStatus } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { useRecentLetters } from '@sola/supporter-sdk/hooks/letters.ts';
import { DEFAULT_RECENT_ACTIVITY_ITEM_LIMIT } from '../../types/recent-activity.ts';
const isFailedLetter = (letter: GraphQLLetter): boolean => letter.status === GraphQLLetterFriendlyStatus.Cancelled || letter.status === GraphQLLetterFriendlyStatus.HasAProblem;
export const getRecentActivityDate = (letter: GraphQLLetter): string | undefined => {
  if (letter.direction === GraphQLLetterDirection.B2S && letter.status === GraphQLLetterFriendlyStatus.Complete) {
    return letter.readyDate ?? letter.createdDate ?? undefined;
  }
  return letter.createdDate ?? letter.readyDate ?? undefined;
};
export const combineRecentLetters = (lettersAvailable: GraphQLLetter[], lettersInTransit: GraphQLLetter[]): GraphQLLetter[] => {
  const lettersById = new Map<string, GraphQLLetter>();
  for (const letter of lettersInTransit) {
    lettersById.set(letter.id, letter);
  }

  // Prefer the available version when an API response contains the same letter
  // in both collections because it represents the later lifecycle state.
  for (const letter of lettersAvailable) {
    lettersById.set(letter.id, letter);
  }
  return [...lettersById.values()].toSorted((a, b) => {
    const aDate = Date.parse(getRecentActivityDate(a) ?? '');
    const bDate = Date.parse(getRecentActivityDate(b) ?? '');
    const normalizedADate = Number.isNaN(aDate) ? 0 : aDate;
    const normalizedBDate = Number.isNaN(bDate) ? 0 : bDate;
    return normalizedBDate - normalizedADate;
  });
};
export const processLetters = (args: {
  letters: GraphQLLetter[];
  t: TFunction<'my'>;
  now?: Date;
  limit?: number;
}) => {
  const {
    letters,
    t,
    now = new Date(),
    limit = DEFAULT_RECENT_ACTIVITY_ITEM_LIMIT
  } = args;
  const today = startOfDay(now);
  const recentLetters = letters.filter(letter => {
    const activityDate = getRecentActivityDate(letter);
    if (!activityDate) {
      return false;
    }
    const parsedActivityDate = new Date(activityDate);
    if (Number.isNaN(parsedActivityDate.getTime())) {
      return false;
    }
    if (parsedActivityDate.getTime() > now.getTime()) {
      return false;
    }
    const daysSinceActivity = differenceInDays(today, startOfDay(parsedActivityDate));
    return daysSinceActivity >= 0 && daysSinceActivity <= 30;
  }).toSorted((a, b) => Date.parse(getRecentActivityDate(b) ?? '') - Date.parse(getRecentActivityDate(a) ?? '')).slice(0, limit);
  const incoming = recentLetters.filter(letter => letter.direction === GraphQLLetterDirection.B2S);
  const outgoing = recentLetters.filter(letter => letter.direction === GraphQLLetterDirection.S2B);
  const unreadIncoming = incoming.filter(letter => letter.status === GraphQLLetterFriendlyStatus.Complete && !letter.isReadBySupporter);
  const incomingSoon = incoming.filter(letter => letter.status !== GraphQLLetterFriendlyStatus.Complete && !isFailedLetter(letter));
  const outgoingOnTheWay = outgoing.filter(letter => letter.status !== GraphQLLetterFriendlyStatus.Complete && !isFailedLetter(letter));
  const subtitleParts: string[] = [];
  const uniqueBeneficiaries = new Map(letters.flatMap(letter => {
    const globalId = letter.beneficiary?.globalId;
    const name = letter.beneficiary?.name;
    return globalId && name ? [[globalId, name] as const] : [];
  }));
  const emptyStateBeneficiaryName = uniqueBeneficiaries.size === 1 ? [...uniqueBeneficiaries.values()][0] : undefined;
  if (outgoingOnTheWay.length > 0) {
    subtitleParts.push(t('my:recentActivity.onTheWay', {
      count: outgoingOnTheWay.length
    }));
  }
  if (unreadIncoming.length > 0) {
    subtitleParts.push(t('my:recentActivity.letter.unreadCount', {
      count: unreadIncoming.length
    }));
  } else if (incomingSoon.length > 0) {
    subtitleParts.push(t('my:recentActivity.letter.inboxCount', {
      count: incomingSoon.length
    }));
  } else if (incoming.length > 0 && outgoingOnTheWay.length === 0 && unreadIncoming.length === 0) {
    subtitleParts.push(t('my:recentActivity.totalDelivered', {
      count: incoming.length
    }));
  }
  return {
    recentLetters,
    subtitle: subtitleParts.join(', '),
    isShowingEmptyState: recentLetters.length === 0,
    emptyStateBeneficiaryName
  };
};
export const useComponentRecentActivityPanelLetters = () => {
  const $ = _c(35);
  const {
    lettersAvailable,
    lettersInTransit,
    isError,
    isLoading,
    isPending
  } = useRecentLetters();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["my"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation(t0);
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[1] !== isError || $[2] !== isLoading || $[3] !== isPending || $[4] !== lettersAvailable || $[5] !== lettersInTransit || $[6] !== t) {
    const letters = combineRecentLetters(lettersAvailable, lettersInTransit);
    const {
      recentLetters,
      subtitle,
      isShowingEmptyState,
      emptyStateBeneficiaryName
    } = processLetters({
      letters,
      t
    });
    t1 = recentLetters;
    t2 = isError && letters.length === 0;
    t3 = (isLoading || isPending) && letters.length === 0;
    if ($[16] !== t) {
      t4 = t("my:recentActivity.title", {
        type: "Letter"
      });
      $[16] = t;
      $[17] = t4;
    } else {
      t4 = $[17];
    }
    t5 = subtitle;
    t6 = isShowingEmptyState;
    if ($[18] !== t) {
      t7 = t("my:recentActivity.noRecentActivityTitle");
      $[18] = t;
      $[19] = t7;
    } else {
      t7 = $[19];
    }
    if ($[20] !== t) {
      t8 = t("my:recentActivity.letter.noRecentActivityMessage");
      $[20] = t;
      $[21] = t8;
    } else {
      t8 = $[21];
    }
    t9 = emptyStateBeneficiaryName ? t("my:recentActivity.letter.writeNamed", {
      name: emptyStateBeneficiaryName
    }) : t("my:recentActivity.letter.write");
    $[1] = isError;
    $[2] = isLoading;
    $[3] = isPending;
    $[4] = lettersAvailable;
    $[5] = lettersInTransit;
    $[6] = t;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
  } else {
    t1 = $[7];
    t2 = $[8];
    t3 = $[9];
    t4 = $[10];
    t5 = $[11];
    t6 = $[12];
    t7 = $[13];
    t8 = $[14];
    t9 = $[15];
  }
  let t10;
  if ($[22] !== t) {
    t10 = t("my:ctas.writeLetter.url");
    $[22] = t;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t1 || $[25] !== t10 || $[26] !== t2 || $[27] !== t3 || $[28] !== t4 || $[29] !== t5 || $[30] !== t6 || $[31] !== t7 || $[32] !== t8 || $[33] !== t9) {
    t11 = {
      recentLetters: t1,
      isError: t2,
      isLoading: t3,
      heading: t4,
      subtitle: t5,
      isShowingEmptyState: t6,
      emptyStateTitle: t7,
      emptyStateMessage: t8,
      emptyStateCtaTitle: t9,
      emptyStateCtaHref: t10
    };
    $[24] = t1;
    $[25] = t10;
    $[26] = t2;
    $[27] = t3;
    $[28] = t4;
    $[29] = t5;
    $[30] = t6;
    $[31] = t7;
    $[32] = t8;
    $[33] = t9;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  return t11;
};